import { useEffect, useRef } from "react"

import {
	activityHeartbeatInterval,
	activityIdleThreshold,
	activityMaxSessionDuration,
	activitySessionIdleTimeout,
} from "@forento/core/utilities/activityTracking"

type Options = {
	/** Tracking runs while this is set; a change starts over as a different user. */
	userId: number | null
	createSession(): Promise<{ id: number }>
	updateSession(input: { id: number; activeDuration: number }): Promise<void>
	/** Sends the closing total while the page is going away, so it has to survive teardown. */
	flushSession(input: { id: number; activeDuration: number }): void
}

const interactionEvents = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"] as const

/**
 * Measures how long a signed in user is actually engaged with the tab.
 *
 * Engagement is the tab being visible and the user having interacted recently, which is what makes the
 * number mean something: a visible but abandoned tab stops accruing time instead of recording a full
 * working day. Time comes from timestamps taken at each transition rather than from counting timer
 * ticks, so throttled background timers cannot skew it, and the heartbeat only runs while engaged, so
 * an idle tab goes quiet rather than writing to the database for as long as it stays open.
 */
export default function useActivityTracking({ userId, createSession, updateSession, flushSession }: Options) {
	const callbacksRef = useRef({ createSession, updateSession, flushSession })
	useEffect(() => {
		callbacksRef.current = { createSession, updateSession, flushSession }
	}, [createSession, flushSession, updateSession])

	useEffect(() => {
		if (userId === null) return
		if (typeof window === "undefined" || typeof document === "undefined") return

		let sessionId: number | null = null
		let sessionStartedAt = Date.now()
		let accruedMs = 0
		let engagedSince: number | null = null
		let disengagedAt: number | null = null
		let lastInteractionAt = Date.now()
		let flushedSeconds = 0
		let isOpening = false
		let isStopped = false
		let heartbeat: ReturnType<typeof setInterval> | null = null
		let idleTimeout: ReturnType<typeof setTimeout> | null = null

		function currentSeconds() {
			const engagedMs = engagedSince === null ? 0 : Date.now() - engagedSince
			return Math.floor((accruedMs + engagedMs) / 1000)
		}

		function flush(mode: "update" | "final") {
			if (sessionId === null) return
			const activeDuration = currentSeconds()
			if (activeDuration === flushedSeconds) return
			flushedSeconds = activeDuration

			if (mode === "final") {
				callbacksRef.current.flushSession({ id: sessionId, activeDuration })
			} else {
				callbacksRef.current.updateSession({ id: sessionId, activeDuration }).catch(() => {})
			}
		}

		function startHeartbeat() {
			if (heartbeat !== null) return
			heartbeat = setInterval(() => {
				if (Date.now() - sessionStartedAt >= activityMaxSessionDuration) {
					// A tab open for days should read as a series of visits, not one implausible session.
					disengage()
					engage()
					return
				}
				flush("update")
			}, activityHeartbeatInterval)
		}

		function stopHeartbeat() {
			if (heartbeat === null) return
			clearInterval(heartbeat)
			heartbeat = null
		}

		/** Reschedules itself rather than resetting on every event, so interaction handlers stay cheap. */
		function scheduleIdleCheck() {
			if (idleTimeout !== null) clearTimeout(idleTimeout)
			idleTimeout = setTimeout(
				() => {
					idleTimeout = null
					if (engagedSince === null) return
					if (Date.now() - lastInteractionAt < activityIdleThreshold) {
						scheduleIdleCheck()
						return
					}
					disengage()
				},
				Math.max(lastInteractionAt + activityIdleThreshold - Date.now(), 0),
			)
		}

		function stopIdleCheck() {
			if (idleTimeout === null) return
			clearTimeout(idleTimeout)
			idleTimeout = null
		}

		function openSession() {
			if (isOpening) return
			isOpening = true
			sessionId = null
			sessionStartedAt = Date.now()
			accruedMs = 0
			flushedSeconds = 0
			callbacksRef.current
				.createSession()
				.then(session => {
					sessionId = session.id
					// The visit can end before the session exists, and that time still counts.
					if (isStopped) flush("update")
				})
				.catch(() => {})
				.finally(() => {
					isOpening = false
				})
		}

		function engage() {
			if (engagedSince !== null) return

			const now = Date.now()
			const isExpired =
				sessionId === null ||
				now - sessionStartedAt >= activityMaxSessionDuration ||
				(disengagedAt !== null && now - disengagedAt >= activitySessionIdleTimeout)
			if (isExpired) openSession()

			engagedSince = now
			disengagedAt = null
			lastInteractionAt = now
			startHeartbeat()
			scheduleIdleCheck()
		}

		function disengage(mode: "update" | "final" = "update") {
			if (engagedSince !== null) {
				accruedMs += Date.now() - engagedSince
				engagedSince = null
				disengagedAt = Date.now()
			}
			stopHeartbeat()
			stopIdleCheck()
			flush(mode)
		}

		function handleInteraction() {
			lastInteractionAt = Date.now()
			if (engagedSince === null && !document.hidden) engage()
		}

		function handleVisibilityChange() {
			// A tab going hidden is indistinguishable from a tab going away, and closing one fires this
			// before `pagehide`, so this flush has to be the one that survives teardown.
			if (document.hidden) disengage("final")
			else handleInteraction()
		}

		function handlePageHide() {
			disengage("final")
		}

		for (const event of interactionEvents) {
			window.addEventListener(event, handleInteraction, { passive: true })
		}
		document.addEventListener("visibilitychange", handleVisibilityChange)
		window.addEventListener("pagehide", handlePageHide)

		// A tab restored into the background should not open a session until it is actually looked at.
		if (!document.hidden) engage()

		return () => {
			isStopped = true
			for (const event of interactionEvents) {
				window.removeEventListener(event, handleInteraction)
			}
			document.removeEventListener("visibilitychange", handleVisibilityChange)
			window.removeEventListener("pagehide", handlePageHide)
			disengage()
		}
	}, [userId])
}
